import { useCallback } from 'react';
import type { IframeInitializationData, PaywallMessageMeta } from '../../types';
import {
  createDashboardNavigationMessage,
  createProfileNavigationMessage,
  createPricingNavigationMessage,
  createInitializationMessage,
} from '../logic';

// AIDEV-NOTE: Extracted paywall navigation actions for cleaner separation of concerns
export const usePaywallActions = (
  sendMessage: (message: any) => void,
  setVisibility: (visibility: "visible" | "hidden") => void,
  appId: string,
  getMeta?: () => PaywallMessageMeta | undefined
) => {
  const showDashboard = useCallback(() => {
    // Use pure function to create navigation message
    const message = createDashboardNavigationMessage(appId);
    if (getMeta) message.meta = getMeta();
    
    // Orchestrate: send message and update visibility
    sendMessage(message);
    setVisibility("visible");
  }, [sendMessage, setVisibility, appId, getMeta]);

  const showCreateProfile = useCallback(() => {
    // Use pure function to create navigation message
    const message = createProfileNavigationMessage(appId);
    if (getMeta) message.meta = getMeta();
    
    // Orchestrate: send message and update visibility
    sendMessage(message);
    setVisibility("visible");
  }, [sendMessage, setVisibility, appId, getMeta]);

  const showPricing = useCallback(() => {
    // Use pure function to create navigation message
    const message = createPricingNavigationMessage(appId);
    if (getMeta) message.meta = getMeta();
    
    // Orchestrate: send message and update visibility
    sendMessage(message);
    setVisibility("visible");
  }, [sendMessage, setVisibility, appId, getMeta]);

  const hide = useCallback(() => {
    setVisibility("hidden");
  }, [setVisibility]);

  const sendIframeInitialization = useCallback(
    (data: IframeInitializationData) => {
      // Use pure function to create initialization message
      const message = createInitializationMessage(data);
      if (getMeta) message.meta = getMeta();
      
      // Orchestrate: send the message
      sendMessage(message);
    },
    [sendMessage, getMeta]
  );

  return {
    showDashboard,
    showCreateProfile,
    showPricing,
    hide,
    sendIframeInitialization,
  };
};
